import { useCallback, useRef, useState } from 'react'

interface IUseLazyImageReturn<T extends HTMLElement> {
  ref: (node: T | null) => void
  isVisible: boolean
}

const ROOT_MARGIN = '200px'

export const useLazyImage = <
  T extends HTMLElement,
>(): IUseLazyImageReturn<T> => {
  const [isVisible, setIsVisible] = useState<boolean>(false)
  const observerRef = useRef<IntersectionObserver | null>(null)

  const ref = useCallback((node: T | null): void => {
    if (observerRef.current) {
      observerRef.current.disconnect()
      observerRef.current = null
    }

    if (!node) {
      return
    }

    if (typeof IntersectionObserver === 'undefined') {
      setIsVisible(true)
      return
    }

    const observer = new IntersectionObserver(
      entries => {
        const [entry] = entries
        if (entry?.isIntersecting) {
          setIsVisible(true)
          observer.disconnect()
          observerRef.current = null
        }
      },
      { rootMargin: ROOT_MARGIN },
    )

    observer.observe(node)
    observerRef.current = observer
  }, [])

  return { ref, isVisible }
}
